// docs.jsx - PyK7VM Developer Hub & API Reference
// Compiled to dist/docs.js via build.mjs. Loaded by docs.php.

const { useState, useEffect, useRef, useCallback, createContext, useContext } = React;
const { Reveal, Aurora, useInView, useScrollRef } = window;
const { Nav, Footer, DiscordCTA } = window;

// ─── GLOBAL LANGUAGE CONTEXT ──────────────────────────────────────────────────
const LangContext = createContext({
  lang: 'curl',
  setLang: () => {},
});

// ─── RAW CODE STRINGS & SYNTAX HIGHLIGHTS (100% VERIFIED) ────────────────────

// Quickstart snippets
const CODE_DECORATOR_BEFORE = `<span style="color:#6a7282"># Original Python source logic</span>
<span style="color:#94a3b8">def</span> <span style="color:#93c5fd">verify_license_key</span>(user_key: <span style="color:#60a5fa">str</span>, hwid: <span style="color:#60a5fa">str</span>) -> <span style="color:#60a5fa">bool</span>:
    expected = hashlib.sha256(f<span style="color:#a3e635">"{hwid}:SECRET_SALT_2026"</span>.encode()).hexdigest()
    <span style="color:#60a5fa">if</span> user_key == expected:
        <span style="color:#60a5fa">return</span> <span style="color:#60a5fa">True</span>
    <span style="color:#60a5fa">return</span> <span style="color:#60a5fa">False</span>`;

const CODE_DECORATOR_AFTER = `<span style="color:#6a7282"># Secured with PyK7VM - auto-detected at build time</span>
<span style="color:#94a3b8">def</span> <span style="color:#93c5fd">verify_license_key</span>(user_key: <span style="color:#60a5fa">str</span>, hwid: <span style="color:#60a5fa">str</span>) -> <span style="color:#60a5fa">bool</span>:
    expected = hashlib.sha256(f<span style="color:#a3e635">"{hwid}:SECRET_SALT_2026"</span>.encode()).hexdigest()
    <span style="color:#60a5fa">if</span> user_key == expected:
        <span style="color:#60a5fa">return</span> <span style="color:#60a5fa">True</span>
    <span style="color:#60a5fa">return</span> <span style="color:#60a5fa">False</span>
<span style="color:#6a7282"># ^ automatically virtualized into polymorphic native VM bytecode</span>`;

const CODE_CLI_BASIC = `<span style="color:#6a7282"># 1. Install CLI compiler utility</span>
pip install pyk7vm-cli

<span style="color:#6a7282"># 2. Virtualize a single script entry point</span>
pyk7vm compile main.py --platform windows --py 3.11 --profile paranoid

<span style="color:#6a7282"># 3. Virtualize an entire multi-file project directory (.zip)</span>
pyk7vm compile ./src --zip --output dist/protected_app.zip --key $PK7VM_API_KEY`;

// Auth header snippet
const CODE_AUTH_HEADER = `<span style="color:#60a5fa">Authorization</span>: Bearer pk7vm_live_9f82a1c0e34789b52a10`;

// 1. Submit Job Snippets (Field: file)
const CODE_SUBMIT_CURL = `curl -X POST https://pyk7vm.com/api/v1/job_submit.php \\
  -H <span style="color:#a3e635">"Authorization: Bearer pk7vm_your_api_key_here"</span> \\
  -F <span style="color:#a3e635">"file=@my_module.py"</span> \\
  -F <span style="color:#a3e635">"module_name=my_module"</span>`;

const CODE_SUBMIT_PYTHON = `<span style="color:#60a5fa">import</span> requests

url = <span style="color:#a3e635">"https://pyk7vm.com/api/v1/job_submit.php"</span>
headers = {<span style="color:#a3e635">"Authorization"</span>: <span style="color:#a3e635">"Bearer pk7vm_your_api_key_here"</span>}

<span style="color:#60a5fa">with</span> <span style="color:#60a5fa">open</span>(<span style="color:#a3e635">"my_module.py"</span>, <span style="color:#a3e635">"rb"</span>) <span style="color:#60a5fa">as</span> f:
    files = {<span style="color:#a3e635">"file"</span>: f}
    data = {<span style="color:#a3e635">"module_name"</span>: <span style="color:#a3e635">"my_module"</span>}
    response = requests.post(url, headers=headers, files=files, data=data)

result = response.json()
print(<span style="color:#a3e635">f"Build Job Submitted: {result['job_id']}"</span>)`;

const CODE_SUBMIT_NODE = `<span style="color:#60a5fa">const</span> fs = require(<span style="color:#a3e635">'fs'</span>);
<span style="color:#60a5fa">const</span> FormData = require(<span style="color:#a3e635">'form-data'</span>);
<span style="color:#60a5fa">const</span> axios = require(<span style="color:#a3e635">'axios'</span>);

<span style="color:#60a5fa">async function</span> <span style="color:#93c5fd">submitBuild</span>() {
  <span style="color:#60a5fa">const</span> form = <span style="color:#60a5fa">new</span> FormData();
  form.append(<span style="color:#a3e635">'file'</span>, fs.createReadStream(<span style="color:#a3e635">'my_module.py'</span>));
  form.append(<span style="color:#a3e635">'module_name'</span>, <span style="color:#a3e635">'my_module'</span>);

  <span style="color:#60a5fa">const</span> res = <span style="color:#60a5fa">await</span> axios.post(<span style="color:#a3e635">'https://pyk7vm.com/api/v1/job_submit.php'</span>, form, {
    headers: {
      ...form.getHeaders(),
      <span style="color:#a3e635">'Authorization'</span>: <span style="color:#a3e635">'Bearer pk7vm_your_api_key_here'</span>
    }
  });
  console.log(<span style="color:#a3e635">'Job ID:'</span>, res.data.job_id);
}`;

const CODE_SUBMIT_RESPONSE = `{
  <span style="color:#34d399">"job_id"</span>:      <span style="color:#fbbf24">"a3f9bc12e4561234a3f9bc12e4561234"</span>,
  <span style="color:#34d399">"status"</span>:      <span style="color:#fbbf24">"queued"</span>,
  <span style="color:#34d399">"module_name"</span>: <span style="color:#fbbf24">"my_module"</span>,
  <span style="color:#34d399">"is_zip"</span>:      <span style="color:#60a5fa">false</span>
}`;

// 2. Check Status Snippets
const CODE_OPTIONS_EXAMPLE = `{
  <span style="color:#34d399">"generate_exe"</span>: <span style="color:#c084fc">true</span>, <span style="color:#6a7282">// standalone .exe instead of .pyd</span>
  <span style="color:#34d399">"window_mode"</span>: <span style="color:#fbbf24">"windowed"</span>, <span style="color:#6a7282">// GUI subsystem: no console window (exe only)</span>
  <span style="color:#34d399">"target_python"</span>: <span style="color:#fbbf24">"3.11"</span>, <span style="color:#34d399">"platform"</span>: <span style="color:#fbbf24">"both"</span>,

  <span style="color:#6a7282">// protection stack (plan-gated: ignored on Free)</span>
  <span style="color:#34d399">"anti_debug"</span>: <span style="color:#c084fc">true</span>, <span style="color:#34d399">"anti_hook"</span>: <span style="color:#c084fc">true</span>, <span style="color:#34d399">"anti_vm"</span>: <span style="color:#c084fc">false</span>,
  <span style="color:#34d399">"ast_mutation"</span>: <span style="color:#c084fc">true</span>, <span style="color:#34d399">"bytecode_mutation"</span>: <span style="color:#c084fc">true</span>,
  <span style="color:#34d399">"mutation_intensity"</span>: <span style="color:#fbbf24">"high"</span>, <span style="color:#34d399">"hwid_binding"</span>: <span style="color:#c084fc">false</span>,

  <span style="color:#6a7282">// function selection</span>
  <span style="color:#34d399">"cython_mode"</span>: <span style="color:#fbbf24">"manual"</span>, <span style="color:#34d399">"cython_targets"</span>: [<span style="color:#fbbf24">"verify_license"</span>],
  <span style="color:#34d399">"vm_skip_targets"</span>: [<span style="color:#fbbf24">"hot_loop"</span>], <span style="color:#34d399">"aot_targets"</span>: [<span style="color:#fbbf24">"parse_config"</span>, <span style="color:#fbbf24">"derive_key"</span>],
  <span style="color:#34d399">"exports"</span>: [<span style="color:#fbbf24">"verify_license:check"</span>],

  <span style="color:#6a7282">// packaging extras (Beta / Premium)</span>
  <span style="color:#34d399">"pyi_stub"</span>: <span style="color:#c084fc">true</span>, <span style="color:#34d399">"wheel"</span>: <span style="color:#c084fc">true</span>, <span style="color:#34d399">"wheel_version"</span>: <span style="color:#fbbf24">"1.2.0"</span>,
  <span style="color:#34d399">"bundle_init"</span>: <span style="color:#c084fc">false</span>, <span style="color:#34d399">"bundle_example"</span>: <span style="color:#c084fc">true</span>,

  <span style="color:#6a7282">// operator alerts (Windows builds)</span>
  <span style="color:#34d399">"webhook_url"</span>: <span style="color:#fbbf24">"https://discord.com/api/webhooks/1234/abcd"</span>,
  <span style="color:#34d399">"webhook_name"</span>: <span style="color:#fbbf24">"MyApp Guard"</span>
}`;

const CODE_STATUS_CURL = `curl -X GET <span style="color:#a3e635">"https://pyk7vm.com/api/v1/job_status.php?job_id=a3f9bc12e4561234a3f9bc12e4561234"</span> \\
  -H <span style="color:#a3e635">"Authorization: Bearer pk7vm_your_api_key_here"</span>`;

const CODE_STATUS_PYTHON = `<span style="color:#60a5fa">import</span> requests

url = <span style="color:#a3e635">"https://pyk7vm.com/api/v1/job_status.php"</span>
headers = {<span style="color:#a3e635">"Authorization"</span>: <span style="color:#a3e635">"Bearer pk7vm_your_api_key_here"</span>}
params = {<span style="color:#a3e635">"job_id"</span>: <span style="color:#a3e635">"a3f9bc12e4561234a3f9bc12e4561234"</span>}

response = requests.get(url, headers=headers, params=params)
status_data = response.json()
print(<span style="color:#a3e635">f"Status: {status_data['status']} ({status_data.get('progress_pct', 0)}%)"</span>)`;

const CODE_STATUS_NODE = `<span style="color:#60a5fa">const</span> axios = require(<span style="color:#a3e635">'axios'</span>);

<span style="color:#60a5fa">async function</span> <span style="color:#93c5fd">checkStatus</span>(jobId) {
  <span style="color:#60a5fa">const</span> res = <span style="color:#60a5fa">await</span> axios.get(<span style="color:#a3e635">\`https://pyk7vm.com/api/v1/job_status.php?job_id=\${jobId}\`</span>, {
    headers: { <span style="color:#a3e635">'Authorization'</span>: <span style="color:#a3e635">'Bearer pk7vm_your_api_key_here'</span> }
  });
  console.log(<span style="color:#a3e635">'Current Status:'</span>, res.data.status, res.data.progress_pct + <span style="color:#a3e635">'%'</span>);
}`;

const CODE_STATUS_RESPONSE = `{
  <span style="color:#34d399">"job_id"</span>:         <span style="color:#fbbf24">"a3f9bc12e4561234a3f9bc12e4561234"</span>,
  <span style="color:#34d399">"status"</span>:         <span style="color:#fbbf24">"complete"</span>,
  <span style="color:#34d399">"module_name"</span>:    <span style="color:#fbbf24">"my_module"</span>,
  <span style="color:#34d399">"is_zip"</span>:         <span style="color:#60a5fa">false</span>,
  <span style="color:#34d399">"progress_pct"</span>:   <span style="color:#c084fc">100</span>,
  <span style="color:#34d399">"progress_label"</span>: <span style="color:#fbbf24">"Build complete and binary signed"</span>,
  <span style="color:#34d399">"created_at"</span>:     <span style="color:#c084fc">1780000000</span>,
  <span style="color:#34d399">"updated_at"</span>:     <span style="color:#c084fc">1780000042</span>,
  <span style="color:#34d399">"download_token"</span>: <span style="color:#fbbf24">"dl_tok_8f93e1b7c4a209e81d774f"</span>
}`;

// 3. Hot Path Snippets
const CODE_HOTPATH_CURL = `curl -X POST https://pyk7vm.com/api/v1/job_hotpath.php \\
  -H <span style="color:#a3e635">"Authorization: Bearer pk7vm_your_api_key_here"</span> \\
  -H <span style="color:#a3e635">"Content-Type: application/json"</span> \\
  -d <span style="color:#a3e635">'{"job_id":"a3f9bc12e4561234a3f9bc12e4561234","hot_paths":["verify_license","encrypt_payload"]}'</span>`;

const CODE_HOTPATH_PYTHON = `<span style="color:#60a5fa">import</span> requests

url = <span style="color:#a3e635">"https://pyk7vm.com/api/v1/job_hotpath.php"</span>
headers = {<span style="color:#a3e635">"Authorization"</span>: <span style="color:#a3e635">"Bearer pk7vm_your_api_key_here"</span>}
payload = {
    <span style="color:#a3e635">"job_id"</span>: <span style="color:#a3e635">"a3f9bc12e4561234a3f9bc12e4561234"</span>,
    <span style="color:#a3e635">"hot_paths"</span>: [<span style="color:#a3e635">"verify_license"</span>, <span style="color:#a3e635">"encrypt_payload"</span>]
}

response = requests.post(url, headers=headers, json=payload)
print(response.json())`;

const CODE_HOTPATH_NODE = `<span style="color:#60a5fa">const</span> axios = require(<span style="color:#a3e635">'axios'</span>);

<span style="color:#60a5fa">async function</span> <span style="color:#93c5fd">confirmHotPaths</span>(jobId, functions) {
  <span style="color:#60a5fa">const</span> res = <span style="color:#60a5fa">await</span> axios.post(<span style="color:#a3e635">'https://pyk7vm.com/api/v1/job_hotpath.php'</span>, {
    job_id: jobId,
    hot_paths: functions
  }, {
    headers: { <span style="color:#a3e635">'Authorization'</span>: <span style="color:#a3e635">'Bearer pk7vm_your_api_key_here'</span> }
  });
  console.log(<span style="color:#a3e635">'Hot paths acknowledged:'</span>, res.data.ok);
}`;

// 4. Download Snippets
const CODE_DOWNLOAD_CURL = `curl -X GET <span style="color:#a3e635">"https://pyk7vm.com/api/v1/job_download.php?job_id=a3f9bc12e4561234a3f9bc12e4561234"</span> \\
  -H <span style="color:#a3e635">"Authorization: Bearer pk7vm_your_api_key_here"</span> \\
  --output my_module_protected.zip`;

const CODE_DOWNLOAD_PYTHON = `<span style="color:#60a5fa">import</span> requests

url = <span style="color:#a3e635">"https://pyk7vm.com/api/v1/job_download.php"</span>
headers = {<span style="color:#a3e635">"Authorization"</span>: <span style="color:#a3e635">"Bearer pk7vm_your_api_key_here"</span>}
params = {<span style="color:#a3e635">"job_id"</span>: <span style="color:#a3e635">"a3f9bc12e4561234a3f9bc12e4561234"</span>}

response = requests.get(url, headers=headers, params=params, stream=<span style="color:#60a5fa">True</span>)
<span style="color:#60a5fa">with</span> <span style="color:#60a5fa">open</span>(<span style="color:#a3e635">"my_module_protected.zip"</span>, <span style="color:#a3e635">"wb"</span>) <span style="color:#60a5fa">as</span> f:
    <span style="color:#60a5fa">for</span> chunk <span style="color:#60a5fa">in</span> response.iter_content(chunk_size=8192):
        f.write(chunk)
print(<span style="color:#a3e635">"Saved my_module_protected.zip"</span>)`;

const CODE_DOWNLOAD_NODE = `<span style="color:#60a5fa">const</span> fs = require(<span style="color:#a3e635">'fs'</span>);
<span style="color:#60a5fa">const</span> axios = require(<span style="color:#a3e635">'axios'</span>);

<span style="color:#60a5fa">async function</span> <span style="color:#93c5fd">downloadArtifact</span>(jobId, outputPath) {
  <span style="color:#60a5fa">const</span> writer = fs.createWriteStream(outputPath);
  <span style="color:#60a5fa">const</span> response = <span style="color:#60a5fa">await</span> axios({
    url: <span style="color:#a3e635">\`https://pyk7vm.com/api/v1/job_download.php?job_id=\${jobId}\`</span>,
    method: <span style="color:#a3e635">'GET'</span>,
    headers: { <span style="color:#a3e635">'Authorization'</span>: <span style="color:#a3e635">'Bearer pk7vm_your_api_key_here'</span> },
    responseType: <span style="color:#a3e635">'stream'</span>
  });
  response.data.pipe(writer);
  <span style="color:#60a5fa">return new</span> Promise((resolve, reject) => {
    writer.on(<span style="color:#a3e635">'finish'</span>, resolve);
    writer.on(<span style="color:#a3e635">'error'</span>, reject);
  });
}`;

// CI/CD Snippets
const CODE_GITHUB_ACTIONS = `<span style="color:#6a7282"># .github/workflows/protect.yml</span>
<span style="color:#93c5fd">name</span>: <span style="color:#a3e635">Protect Python Binaries with PyK7VM</span>

<span style="color:#93c5fd">on</span>:
  <span style="color:#93c5fd">push</span>:
    <span style="color:#93c5fd">tags</span>:
      - <span style="color:#a3e635">'v*'</span>

<span style="color:#93c5fd">jobs</span>:
  <span style="color:#93c5fd">protect-and-release</span>:
    <span style="color:#93c5fd">runs-on</span>: <span style="color:#a3e635">ubuntu-latest</span>
    <span style="color:#93c5fd">steps</span>:
      - <span style="color:#93c5fd">name</span>: <span style="color:#a3e635">Checkout source</span>
        <span style="color:#93c5fd">uses</span>: <span style="color:#a3e635">actions/checkout@v4</span>

      - <span style="color:#93c5fd">name</span>: <span style="color:#a3e635">Set up Python</span>
        <span style="color:#93c5fd">uses</span>: <span style="color:#a3e635">actions/setup-python@v5</span>
        <span style="color:#93c5fd">with</span>:
          <span style="color:#93c5fd">python-version</span>: <span style="color:#a3e635">'3.11'</span>

      - <span style="color:#93c5fd">name</span>: <span style="color:#a3e635">Package source files</span>
        <span style="color:#93c5fd">run</span>: |
          zip -r app_source.zip src/ -x "*.pyc" "__pycache__/*"

      - <span style="color:#93c5fd">name</span>: <span style="color:#a3e635">Submit & Compile via PyK7VM API</span>
        <span style="color:#93c5fd">env</span>:
          <span style="color:#93c5fd">PK7VM_API_KEY</span>: <span style="color:#a3e635">\${{ secrets.PK7VM_API_KEY }}</span>
        <span style="color:#93c5fd">run</span>: |
          python - &lt;&lt; 'EOF'
          import os, time, requests

          key = os.environ["PK7VM_API_KEY"]
          hdr = {"Authorization": f"Bearer {key}"}

          # 1. Submit build
          with open("app_source.zip", "rb") as f:
              res = requests.post("https://pyk7vm.com/api/v1/job_submit.php",
                                  headers=hdr, files={"file": f},
                                  data={"module_name": "app"}).json()
          job_id = res["job_id"]
          print(f"Submitted Job ID: {job_id}")

          # 2. Poll until complete
          while True:
              st = requests.get("https://pyk7vm.com/api/v1/job_status.php",
                                headers=hdr, params={"job_id": job_id}).json()
              print(f"Status: {st['status']} ({st.get('progress_pct', 0)}%)")
              if st["status"] == "complete":
                  break
              if st["status"] == "error":
                  raise RuntimeError(f"Build error: {st.get('error', 'unknown error')}")
              time.sleep(3)

          # 3. Download protected binary
          dl = requests.get("https://pyk7vm.com/api/v1/job_download.php",
                            headers=hdr, params={"job_id": job_id})
          with open("app_protected.zip", "wb") as f:
              f.write(dl.content)
          print("Successfully downloaded protected native build.")
          EOF

      - <span style="color:#93c5fd">name</span>: <span style="color:#a3e635">Upload Protected Artifacts</span>
        <span style="color:#93c5fd">uses</span>: <span style="color:#a3e635">actions/upload-artifact@v4</span>
        <span style="color:#93c5fd">with</span>:
          <span style="color:#93c5fd">name</span>: <span style="color:#a3e635">windows-pyd-protected</span>
          <span style="color:#93c5fd">path</span>: <span style="color:#a3e635">app_protected.zip</span>`;

const CODE_GITLAB_CI = `<span style="color:#6a7282"># .gitlab-ci.yml</span>
<span style="color:#93c5fd">stages</span>:
  - <span style="color:#a3e635">build</span>
  - <span style="color:#a3e635">protect</span>
  - <span style="color:#a3e635">release</span>

<span style="color:#93c5fd">virtualize_python</span>:
  <span style="color:#93c5fd">stage</span>: <span style="color:#a3e635">protect</span>
  <span style="color:#93c5fd">image</span>: <span style="color:#a3e635">python:3.11-slim</span>
  <span style="color:#93c5fd">before_script</span>:
    - <span style="color:#a3e635">pip install requests</span>
  <span style="color:#93c5fd">script</span>:
    - |
      python -c '
      import requests, time, os
      hdr = {"Authorization": "Bearer " + os.environ["PK7VM_API_KEY"]}
      with open("core.py", "rb") as f:
          job = requests.post("https://pyk7vm.com/api/v1/job_submit.php",
                              headers=hdr, files={"file": f},
                              data={"module_name": "core"}).json()
      job_id = job["job_id"]
      while True:
          status = requests.get("https://pyk7vm.com/api/v1/job_status.php",
                                headers=hdr, params={"job_id": job_id}).json()
          if status["status"] == "complete": break
          if status["status"] == "error": exit(1)
          time.sleep(2)
      dl = requests.get("https://pyk7vm.com/api/v1/job_download.php", headers=hdr, params={"job_id": job_id})
      with open("core_protected.zip", "wb") as f: f.write(dl.content)
      '
  <span style="color:#93c5fd">artifacts</span>:
    <span style="color:#93c5fd">paths</span>:
      - <span style="color:#a3e635">core_protected.zip</span>
    <span style="color:#93c5fd">expire_in</span>: <span style="color:#a3e635">1 week</span>
  <span style="color:#93c5fd">only</span>:
    - <span style="color:#a3e635">tags</span>`;

// Standalone SDK Snippet
const CODE_FULL_PYTHON_CLIENT = `<span style="color:#60a5fa">import</span> requests
<span style="color:#60a5fa">import</span> time
<span style="color:#60a5fa">from</span> pathlib <span style="color:#60a5fa">import</span> Path

<span style="color:#60a5fa">class</span> <span style="color:#93c5fd">PyK7VMClient</span>:
    <span style="color:#60a5fa">def</span> <span style="color:#93c5fd">__init__</span>(self, api_key: <span style="color:#60a5fa">str</span>, base_url: <span style="color:#a3e635">"https://pyk7vm.com/api/v1"</span>):
        self.base_url = base_url.rstrip(<span style="color:#a3e635">"/"</span>)
        self.headers = {<span style="color:#a3e635">"Authorization"</span>: f<span style="color:#a3e635">"Bearer {api_key}"</span>}

    <span style="color:#60a5fa">def</span> <span style="color:#93c5fd">protect</span>(self, file_path: <span style="color:#60a5fa">str</span>, output_path: <span style="color:#60a5fa">str</span>, module_name: <span style="color:#60a5fa">str</span> = <span style="color:#a3e635">"main"</span>) -> Path:
        path = Path(file_path)
        <span style="color:#60a5fa">with</span> open(path, <span style="color:#a3e635">"rb"</span>) <span style="color:#60a5fa">as</span> f:
            sub = requests.post(
                f<span style="color:#a3e635">"{self.base_url}/job_submit.php"</span>,
                headers=self.headers,
                files={<span style="color:#a3e635">"file"</span>: (path.name, f)},
                data={<span style="color:#a3e635">"module_name"</span>: module_name}
            ).json()

        job_id = sub[<span style="color:#a3e635">"job_id"</span>]
        <span style="color:#60a5fa">while</span> <span style="color:#60a5fa">True</span>:
            st = requests.get(f<span style="color:#a3e635">"{self.base_url}/job_status.php"</span>,
                              headers=self.headers, params={<span style="color:#a3e635">"job_id"</span>: job_id}).json()
            <span style="color:#60a5fa">if</span> st[<span style="color:#a3e635">"status"</span>] == <span style="color:#a3e635">"complete"</span>:
                <span style="color:#60a5fa">break</span>
            <span style="color:#60a5fa">elif</span> st[<span style="color:#a3e635">"status"</span>] == <span style="color:#a3e635">"error"</span>:
                <span style="color:#60a5fa">raise</span> RuntimeError(f<span style="color:#a3e635">"Build failed: {st.get('error', 'unknown error')}"</span>)
            time.sleep(2)

        dl = requests.get(f<span style="color:#a3e635">"{self.base_url}/job_download.php"</span>,
                          headers=self.headers, params={<span style="color:#a3e635">"job_id"</span>: job_id}, stream=<span style="color:#60a5fa">True</span>)
        out = Path(output_path)
        <span style="color:#60a5fa">with</span> open(out, <span style="color:#a3e635">"wb"</span>) <span style="color:#60a5fa">as</span> f:
            <span style="color:#60a5fa">for</span> chunk <span style="color:#60a5fa">in</span> dl.iter_content(8192):
                f.write(chunk)
        <span style="color:#60a5fa">return</span> out

<span style="color:#6a7282"># Example invocation:</span>
<span style="color:#6a7282"># client = PyK7VMClient(api_key="pk7vm_live_...")</span>
<span style="color:#6a7282"># client.protect("app.py", "app_protected.zip", module_name="app")</span>`;

// Workflow ASCII
const CODE_WORKFLOW_FLOW = `<span style="color:#60a5fa">POST</span> /api/v1/job_submit.php          <span style="color:#6a7282">→ Returns { job_id: "a3f9...", status: "queued" }</span>
  <span style="color:#93c5fd">│</span>
  <span style="color:#93c5fd">▼</span>  <span style="color:#6a7282">[Async Polling Interval: 2-3s]</span>
<span style="color:#60a5fa">GET</span>  /api/v1/job_status.php?job_id=  <span style="color:#6a7282">→ status: "building", progress_pct: 45</span>
  <span style="color:#93c5fd">│</span>
  <span style="color:#93c5fd">▼</span>  <span style="color:#6a7282">(Optional: Cython hot path compilation)</span>
<span style="color:#60a5fa">POST</span> /api/v1/job_hotpath.php         <span style="color:#6a7282">→ { ok: true, hot_paths: ["verify_license"] }</span>
  <span style="color:#93c5fd">│</span>
  <span style="color:#93c5fd">▼</span>
<span style="color:#60a5fa">GET</span>  /api/v1/job_status.php?job_id=  <span style="color:#6a7282">→ status: "complete", download_token: "dl_tok_..."</span>
  <span style="color:#93c5fd">│</span>
  <span style="color:#93c5fd">▼</span>
<span style="color:#60a5fa">GET</span>  /api/v1/job_download.php?job_id=<span style="color:#6a7282">→ Streams native compiled .zip (.pyd/.so)</span>`;

// Error JSON shape
const CODE_ERROR_RESPONSE = `{
  <span style="color:#f87171">"error"</span>:   <span style="color:#fbbf24">"Invalid API authentication key provided."</span>,
  <span style="color:#f87171">"code"</span>:    <span style="color:#fbbf24">"unauthorized"</span>,
  <span style="color:#f87171">"status"</span>:  <span style="color:#c084fc">401</span>
}`;

// ─── SVG ICONS HELPER ───────────────────────────────────────────────────────

function NavIcon({ name, color, size = 14 }) {
  const props = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: color || 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    style: { flexShrink: 0 },
  };

  switch (name) {
    case 'book-open':
    case 'book':
      return (
        <svg {...props}>
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        </svg>
      );
    case 'layers':
      return (
        <svg {...props}>
          <polygon points="12 2 2 7 12 12 22 7 12 2" />
          <polyline points="2 17 12 22 22 17" />
          <polyline points="2 12 12 17 22 12" />
        </svg>
      );
    case 'cpu':
      return (
        <svg {...props}>
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <rect x="9" y="9" width="6" height="6" />
          <line x1="9" y1="1" x2="9" y2="4" />
          <line x1="15" y1="1" x2="15" y2="4" />
          <line x1="9" y1="20" x2="9" y2="23" />
          <line x1="15" y1="20" x2="15" y2="23" />
          <line x1="20" y1="9" x2="23" y2="9" />
          <line x1="20" y1="14" x2="23" y2="14" />
          <line x1="1" y1="9" x2="4" y2="9" />
          <line x1="1" y1="14" x2="4" y2="14" />
        </svg>
      );
    case 'shield-check':
      return (
        <svg {...props}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <polyline points="9 12 11 14 15 10" />
        </svg>
      );
    case 'rocket':
      return (
        <svg {...props}>
          <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
          <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
        </svg>
      );
    case 'terminal':
      return (
        <svg {...props}>
          <polyline points="4 17 10 11 4 5" />
          <line x1="12" y1="19" x2="20" y2="19" />
        </svg>
      );
    case 'lock':
      return (
        <svg {...props}>
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      );
    case 'upload':
      return (
        <svg {...props}>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      );
    case 'activity':
      return (
        <svg {...props}>
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      );
    case 'zap':
      return (
        <svg {...props}>
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      );
    case 'download':
      return (
        <svg {...props}>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      );
    case 'git-branch':
      return (
        <svg {...props}>
          <line x1="6" y1="3" x2="6" y2="15" />
          <circle cx="18" cy="6" r="3" />
          <circle cx="6" cy="18" r="3" />
          <path d="M18 9a9 9 0 0 1-9 9" />
        </svg>
      );
    case 'github':
      return (
        <svg {...props}>
          <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />
          <path d="M9 18c-4.51 2-5-2-7-2" />
        </svg>
      );
    case 'gitlab':
      return (
        <svg {...props}>
          <path d="m22 13.29-1.33-4.1a1.26 1.26 0 0 0-.25-.43 1.34 1.34 0 0 0-.41-.33 1.45 1.45 0 0 0-.53-.16 1.49 1.49 0 0 0-.56.06 1.39 1.39 0 0 0-.46.27 1.34 1.34 0 0 0-.33.43L16.4 14H7.6L5.87 8.93a1.34 1.34 0 0 0-.33-.43 1.39 1.39 0 0 0-.46-.27 1.49 1.49 0 0 0-.56-.06 1.45 1.45 0 0 0-.53.16 1.34 1.34 0 0 0-.41.33 1.26 1.26 0 0 0-.25.43L2 13.29a.9.9 0 0 0 .32.93l9.35 6.94a.63.63 0 0 0 .66 0l9.35-6.94a.9.9 0 0 0 .32-.93Z" />
        </svg>
      );
    case 'code':
      return (
        <svg {...props}>
          <polyline points="16 18 22 12 16 6" />
          <polyline points="8 6 2 12 8 18" />
        </svg>
      );
    case 'gauge':
      return (
        <svg {...props}>
          <path d="m12 14 4-4" />
          <path d="M3.34 19a10 10 0 1 1 17.32 0" />
        </svg>
      );
    case 'alert-triangle':
      return (
        <svg {...props}>
          <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case 'check':
      return (
        <svg {...props}>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    case 'close':
    case 'x':
      return (
        <svg {...props}>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      );
    case 'chevron-down':
      return (
        <svg {...props}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      );
    default:
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
  }
}

// ─── UI SUB-COMPONENTS ──────────────────────────────────────────────────────

function CopyButton({ textToCopy }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    const temp = document.createElement('div');
    temp.innerHTML = textToCopy;
    const cleanText = temp.textContent || temp.innerText || '';
    navigator.clipboard.writeText(cleanText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [textToCopy]);

  return (
    <button
      onClick={handleCopy}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: copied ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.06)',
        border: `1px solid ${copied ? 'rgba(16,185,129,0.5)' : 'rgba(255,255,255,0.12)'}`,
        color: copied ? '#34d399' : '#cbd5e1',
        borderRadius: 6,
        padding: '5px 11px',
        fontSize: 11.5,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all .2s ease',
        fontFamily: 'var(--font-ui)',
        minHeight: 30,
      }}
      title="Copy code to clipboard"
    >
      {copied ? (
        <>
          <NavIcon name="check" color="#34d399" size={12} />
          <span>Copied!</span>
        </>
      ) : (
        <>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          <span>Copy</span>
        </>
      )}
    </button>
  );
}

function MethodBadge({ method }) {
  const styles = {
    GET:    { bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.4)', text: '#34d399' },
    POST:   { bg: 'rgba(59,130,246,0.15)', border: 'rgba(59,130,246,0.4)', text: '#60a5fa' },
    DELETE: { bg: 'rgba(248,113,113,0.15)', border: 'rgba(248,113,113,0.4)', text: '#f87171' },
    PUT:    { bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.4)', text: '#fbbf24' },
  };
  const c = styles[method] || { bg: 'rgba(245,241,232,0.1)', border: 'var(--border)', text: 'var(--text)' };

  return (
    <span style={{
      background: c.bg,
      border: `1px solid ${c.border}`,
      color: c.text,
      fontSize: 11,
      fontWeight: 700,
      padding: '3px 9px',
      borderRadius: 5,
      letterSpacing: 0.8,
      fontFamily: 'var(--font-code)',
      marginRight: 10,
      display: 'inline-block',
      flexShrink: 0,
    }}>
      {method}
    </span>
  );
}

function InlineCode({ children }) {
  return (
    <code style={{
      background: 'var(--surface-hover)',
      border: '1px solid var(--border)',
      borderRadius: 5,
      padding: '2px 7px',
      fontFamily: 'var(--font-code)',
      fontSize: 12.5,
      color: 'var(--brand-soft)',
      wordBreak: 'break-word',
    }}>
      {children}
    </code>
  );
}

function SingleCodeBlock({ code, title = null }) {
  return (
    <div className="docs-code-box stay-dark" style={{
      background: '#090a12',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      borderRadius: 12,
      overflow: 'hidden',
      margin: '14px 0 20px',
      maxWidth: '100%',
      boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
    }}>
      <div className="docs-code-head" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 16px',
        background: '#11131f',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#3b82f6', boxShadow: '0 0 8px #3b82f6' }} />
          <span style={{ fontSize: 11.5, color: '#94a3b8', fontFamily: 'var(--font-code)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title || 'Terminal / Response'}
          </span>
        </div>
        <CopyButton textToCopy={code} />
      </div>
      <pre className="custom-scrollbar docs-code-pre stay-dark" style={{
        fontFamily: 'var(--font-code)',
        fontSize: 12.5,
        lineHeight: 1.75,
        padding: '18px 20px',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        margin: 0,
        color: '#f8fafc',
        maxWidth: '100%',
        whiteSpace: 'pre',
        wordBreak: 'normal',
        background: '#090a12',
      }} dangerouslySetInnerHTML={{ __html: code }} />
    </div>
  );
}

function MultiLangCodeBlock({ snippets, title = 'Request Snippet' }) {
  const { lang, setLang } = useContext(LangContext);
  const availableLangs = Object.keys(snippets);
  const currentLang = snippets[lang] ? lang : availableLangs[0];
  const activeCode = snippets[currentLang] || '';

  const labels = {
    curl: 'cURL',
    python: 'Python (requests)',
    node: 'Node.js (Axios)',
    plain: 'Plain Python',
    decorated: 'Auto-Virtualized',
  };

  return (
    <div className="docs-code-box stay-dark" style={{
      background: '#090a12',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      borderRadius: 12,
      overflow: 'hidden',
      margin: '16px 0 24px',
      boxShadow: '0 16px 40px rgba(0,0,0,0.35), 0 0 20px rgba(59,130,246,0.06)',
      maxWidth: '100%',
    }}>
      <div className="docs-code-head" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 12px',
        background: '#11131f',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        flexWrap: 'wrap',
        gap: 8,
      }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {availableLangs.map((key) => {
            const isActive = currentLang === key;
            return (
              <button
                key={key}
                onClick={() => setLang(key)}
                style={{
                  background: isActive ? 'rgba(59,130,246,0.22)' : 'transparent',
                  border: `1px solid ${isActive ? 'rgba(59,130,246,0.55)' : 'transparent'}`,
                  color: isActive ? '#93c5fd' : '#94a3b8',
                  borderRadius: 6,
                  padding: '5px 12px',
                  fontSize: 12,
                  fontWeight: isActive ? 600 : 400,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-ui)',
                  transition: 'all .15s ease',
                  minHeight: 30,
                }}
              >
                {labels[key] || key.toUpperCase()}
              </button>
            );
          })}
        </div>
        <CopyButton textToCopy={activeCode} />
      </div>
      <pre className="custom-scrollbar docs-code-pre stay-dark" style={{
        fontFamily: 'var(--font-code)',
        fontSize: 12.5,
        lineHeight: 1.75,
        padding: '18px 20px',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        margin: 0,
        color: '#f8fafc',
        maxWidth: '100%',
        whiteSpace: 'pre',
        wordBreak: 'normal',
        background: '#090a12',
      }} dangerouslySetInnerHTML={{ __html: activeCode }} />
    </div>
  );
}

function EndpointCard({ method, path, desc }) {
  return (
    <div className="docs-endpoint-card" style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 18px',
      background: 'var(--surface)',
      border: '1px solid var(--border-strong)',
      borderRadius: 10,
      marginBottom: 16,
      flexWrap: 'wrap',
      gap: 12,
      maxWidth: '100%',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', fontFamily: 'var(--font-code)', fontSize: 13.5, flexWrap: 'wrap', gap: 6 }}>
        <MethodBadge method={method} />
        <span style={{ color: 'var(--text)', fontWeight: 600, wordBreak: 'break-all' }}>{path}</span>
      </div>
      {desc && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{desc}</span>}
    </div>
  );
}

function ParamTable({ children }) {
  return (
    <div className="table-responsive-wrapper custom-scrollbar" style={{
      borderRadius: 10,
      border: '1px solid var(--border)',
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      marginTop: 16,
      marginBottom: 24,
      background: 'var(--surface)',
      maxWidth: '100%',
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
        <thead>
          <tr style={{ background: 'var(--surface-hover)' }}>
            <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>Field</th>
            <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>Type</th>
            <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>Required</th>
            <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>Description</th>
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function ParamRow({ name, type, required, desc }) {
  return (
    <tr>
      <td style={{ padding: '11px 14px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--font-code)', fontSize: 12.5, color: 'var(--text)', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
        {name}
      </td>
      <td style={{ padding: '12px 14px', fontFamily: 'var(--font-code)', fontSize: 12, color: 'var(--text-muted)', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
        {type}
      </td>
      <td style={{ padding: '12px 14px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
        {required ? (
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#f87171', background: 'rgba(239,68,68,0.1)', padding: '2px 6px', borderRadius: 4 }}>Required</span>
        ) : (
          <span style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', color: 'var(--text-muted)', background: 'rgba(245,241,232,0.05)', padding: '2px 6px', borderRadius: 4 }}>Optional</span>
        )}
      </td>
      <td style={{ padding: '12px 14px', color: 'var(--text-dim)', fontSize: 13.5, lineHeight: 1.6, verticalAlign: 'top' }}>
        {desc}
      </td>
    </tr>
  );
}

// ─── VISUAL LIFECYCLE STEPPER ───────────────────────────────────────────────

function LifecycleStepper({ onNavigate }) {
  const steps = [
    {
      num: 1,
      targetId: 'submit',
      title: 'Submit Build Job',
      method: 'POST',
      endpoint: '/api/v1/job_submit.php',
      color: '#3b82f6',
      desc: 'Client uploads Python source (.py) or project archive (.zip) via multipart/form-data with optional Cython hot path configuration.',
      responsePill: 'Returns { job_id: "a3f9...", status: "queued" }',
    },
    {
      num: 2,
      targetId: 'status',
      title: 'Poll Compilation Progress',
      method: 'GET',
      endpoint: '/api/v1/job_status.php?job_id=...',
      color: '#fbbf24',
      desc: 'Client polls daemon progress in a 2–3s loop. Status transitions from queued to building as AST virtualization and C++ compilation run.',
      responsePill: 'status: "building" | progress_pct: 45%',
    },
    {
      num: 3,
      targetId: 'hotpath',
      title: 'Confirm Hot Paths (Optional)',
      method: 'POST',
      endpoint: '/api/v1/job_hotpath.php',
      color: '#c084fc',
      desc: 'If prescan triggers hot_path_pending status, post the selected function array to compile performance-critical routines natively via Cython.',
      responsePill: '{ ok: true, hot_paths: ["verify_license"] }',
    },
    {
      num: 4,
      targetId: 'status',
      title: 'Await Job Completion',
      method: 'GET',
      endpoint: '/api/v1/job_status.php?job_id=...',
      color: '#60a5fa',
      desc: 'Compiler completes bytecode encryption, VM opcode mutation, and anti-tamper envelope wrapping. Issues single-use download_token.',
      responsePill: 'status: "complete" | download_token: "dl_tok_..."',
    },
    {
      num: 5,
      targetId: 'download',
      title: 'Stream Protected Artifact',
      method: 'GET',
      endpoint: '/api/v1/job_download.php?job_id=...',
      color: '#34d399',
      desc: 'Client streams the compiled .zip archive containing native binaries (.pyd / .so). File unlinks immediately on completion with 1h TTL.',
      responsePill: 'Streams binary package (Content-Type: application/zip)',
    },
  ];

  return (
    <div style={{
      position: 'relative',
      marginTop: 24,
      marginBottom: 32,
      maxWidth: '100%',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, position: 'relative' }}>
        {/* Continuous Connecting Spine */}
        <div style={{
          position: 'absolute',
          left: 17,
          top: 24,
          bottom: 24,
          width: 2,
          background: 'linear-gradient(180deg, #3b82f6 0%, #fbbf24 30%, #c084fc 60%, #34d399 100%)',
          opacity: 0.45,
          borderRadius: 2,
          zIndex: 0,
        }} />

        {steps.map((step) => (
          <div
            key={step.num}
            style={{
              display: 'flex',
              gap: 'clamp(10px, 2.5vw, 16px)',
              alignItems: 'flex-start',
              position: 'relative',
              zIndex: 1,
              maxWidth: '100%',
            }}
          >
            {/* Centered Step Bubble */}
            <div style={{
              width: 36,
              height: 36,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 12,
            }}>
              <div style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: 'var(--surface)',
                border: `2px solid ${step.color}`,
                boxShadow: `0 0 12px ${step.color}66`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 800,
                color: step.color,
                fontFamily: 'var(--font-ui)',
              }}>
                {step.num}
              </div>
            </div>

            {/* Step Card */}
            <div className="card panel docs-lifecycle-card" style={{
              flex: 1,
              minWidth: 0,
              background: 'var(--surface)',
              border: `1px solid var(--border-strong)`,
              borderRadius: 12,
              padding: 'clamp(14px, 3vw, 20px)',
              backdropFilter: 'blur(12px)',
              transition: 'all .2s ease',
            }}>
              {/* Header row */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                marginBottom: 10,
              }}>
                <span style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'clamp(14px, 2vw, 15.5px)',
                  fontWeight: 700,
                  color: 'var(--text)',
                  letterSpacing: -0.2,
                }}>
                  {step.title}
                </span>

                {onNavigate && (
                  <button
                    onClick={() => onNavigate(step.targetId)}
                    className="docs-stepper-btn"
                    style={{
                      background: 'var(--surface-hover)',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      padding: '4px 10px',
                      color: 'var(--text-dim)',
                      fontSize: 11.5,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'var(--font-ui)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      transition: 'all .15s ease',
                      minHeight: 30,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'var(--text)';
                      e.currentTarget.style.borderColor = step.color;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'var(--text-dim)';
                      e.currentTarget.style.borderColor = 'var(--border)';
                    }}
                  >
                    <span>View Docs</span>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
                  </button>
                )}
              </div>

              {/* Endpoint Chip */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <span style={{
                  fontFamily: 'var(--font-code)',
                  fontSize: 11,
                  fontWeight: 800,
                  color: step.method === 'POST' ? '#10b981' : '#3b82f6',
                  background: step.method === 'POST' ? 'rgba(16,185,129,0.12)' : 'rgba(59,130,246,0.12)',
                  padding: '2px 7px',
                  borderRadius: 4,
                  flexShrink: 0,
                }}>
                  {step.method}
                </span>
                <span style={{
                  fontFamily: 'var(--font-code)',
                  fontSize: 12.5,
                  color: 'var(--brand-soft)',
                  background: 'rgba(59,130,246,0.08)',
                  padding: '3px 9px',
                  borderRadius: 5,
                  border: '1px solid rgba(59,130,246,0.2)',
                  wordBreak: 'break-all',
                }}>
                  {step.endpoint}
                </span>
              </div>

              {/* Description */}
              <p style={{
                fontSize: 13.5,
                color: 'var(--text-dim)',
                lineHeight: 1.65,
                margin: '0 0 12px',
                fontFamily: 'var(--font-ui)',
              }}>
                {step.desc}
              </p>

              {/* Return Payload Pill */}
              <div className="docs-return-pill" style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: 'var(--bg)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: 12,
                fontFamily: 'var(--font-code)',
                color: 'var(--text-dim)',
                overflow: 'hidden',
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={step.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{step.responsePill}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── COLOR-THEMED & ICON-ORGANIZED TABLE OF CONTENTS ────────────────────────

const TOC_GROUPS = [
  {
    category: 'Getting Started',
    categoryIcon: 'book-open',
    themeColor: '#10b981',
    themeSoft: '#34d399',
    badgeBg: 'rgba(16,185,129,0.1)',
    items: [
      { id: 'overview',       label: 'Overview & Base URL', icon: 'rocket' },
      { id: 'quickstart-cli', label: 'Quickstart & CLI Tool', icon: 'terminal' },
      { id: 'authentication', label: 'Authentication & Keys', icon: 'lock' },
    ],
  },
  {
    category: 'REST API Reference',
    categoryIcon: 'layers',
    themeColor: '#3b82f6',
    themeSoft: '#93c5fd',
    badgeBg: 'rgba(59,130,246,0.1)',
    items: [
      { id: 'submit',         label: 'POST /job_submit.php', icon: 'upload' },
      { id: 'build-options',  label: 'Build Options Reference', icon: 'sliders' },
      { id: 'status',         label: 'GET /job_status.php',  icon: 'activity' },
      { id: 'hotpath',        label: 'POST /job_hotpath.php', icon: 'zap' },
      { id: 'download',       label: 'GET /job_download.php', icon: 'download' },
      { id: 'workflow',       label: 'Full Lifecycle Flow',  icon: 'git-branch' },
    ],
  },
  {
    category: 'CI/CD Blueprints',
    categoryIcon: 'cpu',
    themeColor: '#a855f7',
    themeSoft: '#c084fc',
    badgeBg: 'rgba(168,85,247,0.1)',
    items: [
      { id: 'github-actions', label: 'GitHub Actions Recipe', icon: 'github' },
      { id: 'gitlab-ci',      label: 'GitLab CI/CD Pipeline', icon: 'gitlab' },
    ],
  },
  {
    category: 'Platform Reference',
    categoryIcon: 'shield-check',
    themeColor: '#f59e0b',
    themeSoft: '#fbbf24',
    badgeBg: 'rgba(245,158,11,0.1)',
    items: [
      { id: 'python-sdk',     label: 'Python Client SDK',    icon: 'code' },
      { id: 'rate-limits',    label: 'Rate Limits (3 Plans)', icon: 'gauge' },
      { id: 'errors',         label: 'Error Codes Matrix',   icon: 'alert-triangle' },
    ],
  },
];

// Flat list for scroll observer
const ALL_TOC_IDS = TOC_GROUPS.flatMap((g) => g.items.map((i) => i.id));

// ─── MAIN DOCUMENTATION LAYOUT ──────────────────────────────────────────────

function DocsLayout() {
  const [activeId, setActiveId] = useState('overview');
  const [mobileSelect, setMobileSelect] = useState('overview');
  const itemRefs = useRef({});
  const sidebarRef = useRef(null);
  const handleScroll = useCallback(() => {
    const scrollPos = window.pageYOffset || document.documentElement.scrollTop || 0;
    const headerOffset = 150;

    let currentActive = ALL_TOC_IDS[0];
    for (let i = 0; i < ALL_TOC_IDS.length; i++) {
      const id = ALL_TOC_IDS[i];
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + scrollPos - headerOffset;
        if (scrollPos >= top) {
          currentActive = id;
        }
      }
    }

    if ((window.innerHeight + scrollPos) >= document.documentElement.scrollHeight - 80) {
      currentActive = ALL_TOC_IDS[ALL_TOC_IDS.length - 1];
    }

    setActiveId(currentActive);
    setMobileSelect(currentActive);
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    let lenisTimer = setInterval(() => {
      if (window.__lenis) {
        window.__lenis.on('scroll', handleScroll);
        clearInterval(lenisTimer);
      }
    }, 100);

    let animId;
    let lastY = -1;
    const loop = () => {
      const y = window.pageYOffset || document.documentElement.scrollTop || 0;
      if (y !== lastY) {
        lastY = y;
        handleScroll();
      }
      animId = requestAnimationFrame(loop);
    };
    animId = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (window.__lenis) window.__lenis.off('scroll', handleScroll);
      clearInterval(lenisTimer);
      cancelAnimationFrame(animId);
    };
  }, [handleScroll]);

  // Sidebar Auto-Follow without hijacking window scroll
  useEffect(() => {
    const activeEl = itemRefs.current[activeId];
    const sidebarEl = sidebarRef.current;
    if (activeEl && sidebarEl) {
      const activeTop = activeEl.offsetTop;
      const activeHeight = activeEl.offsetHeight;
      const sidebarHeight = sidebarEl.clientHeight;
      const sidebarScroll = sidebarEl.scrollTop;

      if (activeTop < sidebarScroll + 30) {
        sidebarEl.scrollTo({ top: Math.max(0, activeTop - 30), behavior: 'smooth' });
      } else if (activeTop + activeHeight > sidebarScroll + sidebarHeight - 30) {
        sidebarEl.scrollTo({ top: activeTop + activeHeight - sidebarHeight + 30, behavior: 'smooth' });
      }
    }
  }, [activeId]);

  const scrollTo = useCallback((id) => {
    setActiveId(id);
    setMobileSelect(id);

    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) {
      window.__lenis.scrollTo(el, { offset: -90, duration: 0.8 });
    } else {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }
  }, []);

  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024 && drawerOpen) {
        setDrawerOpen(false);
      }
    };
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, [drawerOpen]);

  const handleMobileSelect = (targetId) => {
    setMobileSelect(targetId);
    setDrawerOpen(false);
    scrollTo(targetId);
  };

  const currentItem = ALL_TOC_IDS.includes(activeId)
    ? TOC_GROUPS.flatMap(g => g.items).find(i => i.id === activeId)
    : null;

  return (
    <div style={{ paddingTop: 64, position: 'relative', overflowX: 'clip', width: '100%', maxWidth: '100vw' }}>
      <style>{`
        /* Custom Glassmorphic Scrollbar */
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
          height: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(245, 241, 232, 0.12);
          border-radius: 999px;
          transition: background 0.2s, box-shadow 0.2s;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.6);
          box-shadow: 0 0 8px rgba(59, 130, 246, 0.4);
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(245, 241, 232, 0.15) transparent;
        }

        .docs-section {
          scroll-margin-top: 130px;
          padding-bottom: clamp(48px, 6vw, 72px);
          border-bottom: 1px solid var(--border);
          margin-bottom: clamp(48px, 6vw, 72px);
          width: 100%;
          max-width: 100%;
        }

        @media (max-width: 1024px) {
          .docs-sidebar { display: none !important; }
          .docs-grid {
            grid-template-columns: 1fr !important;
            padding: 0 clamp(16px, 4vw, 32px) !important;
            gap: 20px !important;
          }
          .docs-main {
            padding-left: 0 !important;
            max-width: 100% !important;
            width: 100% !important;
          }
          .mobile-toc-bar { display: block !important; }
        }
        @media (min-width: 1025px) {
          .mobile-toc-bar { display: none !important; }
          .toc-bottom-drawer { display: none !important; }
          .toc-drawer-backdrop { display: none !important; }
          .docs-sidebar { display: block !important; }
          .docs-grid {
            display: grid !important;
            grid-template-columns: clamp(260px, 19vw, 310px) minmax(0, 1fr) !important;
          }
        }

        /* Screen-Adaptive Density */
        @media (max-height: 850px) {
          .docs-sidebar {
            padding-top: 20px !important;
            padding-bottom: 24px !important;
          }
          .toc-group-box {
            margin-bottom: 18px !important;
          }
          .toc-item-btn {
            padding: 5px 8px !important;
            font-size: 12.5px !important;
            margin-bottom: 1px !important;
          }
          .icon-chip {
            width: 22px !important;
            height: 22px !important;
          }
        }

        .toc-item-btn {
          transition: transform .2s cubic-bezier(.2,.7,.2,1), background .2s ease, color .2s ease, border-left .2s ease;
        }
        .toc-item-btn:hover {
          transform: translateX(4px);
          background: rgba(245,241,232,0.04) !important;
          color: var(--text) !important;
        }
        .toc-item-btn:hover .icon-chip {
          transform: scale(1.08);
        }
        .icon-chip {
          transition: transform .2s ease, background .2s ease, box-shadow .2s ease;
        }

        /* Slide-up TOC Bottom Drawer on Mobile */
        .toc-bottom-drawer {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          max-height: 80vh;
          background: rgba(10, 10, 16, 0.98);
          backdrop-filter: blur(28px);
          -webkit-backdrop-filter: blur(28px);
          border-top: 1px solid var(--border-strong);
          border-radius: 20px 20px 0 0;
          z-index: 1050;
          display: flex;
          flex-direction: column;
          box-shadow: 0 -10px 40px rgba(0,0,0,0.7);
        }
        html.light .toc-bottom-drawer {
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 -10px 40px rgba(15, 23, 42, 0.15);
        }
        html.light .toc-item-btn:hover {
          background: rgba(15, 23, 42, 0.04) !important;
        }
        html.light .docs-code-box,
        html.light .docs-endpoint-card {
          background: #ffffff !important;
          border-color: rgba(15, 23, 42, 0.08) !important;
          box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05) !important;
        }
        html.light .docs-code-head {
          background: #f8fafc !important;
          border-bottom-color: rgba(15, 23, 42, 0.08) !important;
        }
        html.light .docs-code-pre {
          background: #f8fafc !important;
          color: #0f172a !important;
        }
        html.light .mobile-toc-bar {
          background: rgba(255, 255, 255, 0.94) !important;
          border-bottom-color: rgba(15, 23, 42, 0.08) !important;
        }
      `}</style>

      {/* Mobile Sticky Quick-Jumper Bar (No Emojis, Pure SVGs) */}
      <div className="mobile-toc-bar" style={{
        position: 'sticky',
        top: 64,
        zIndex: 40,
        background: 'rgba(8,8,13,0.94)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid var(--border)',
        padding: '10px clamp(16px, 4vw, 24px)',
        width: '100%',
        boxSizing: 'border-box',
      }}>
        <button
          onClick={() => setDrawerOpen(true)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'rgba(245,241,232,0.03)',
            border: '1px solid var(--border-strong)',
            color: 'var(--text)',
            padding: '10px 14px',
            borderRadius: 10,
            fontSize: 13.5,
            fontFamily: 'var(--font-ui)',
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
            <span style={{ color: 'var(--brand-soft)', display: 'inline-flex', alignItems: 'center' }}>
              <NavIcon name="book-open" color="var(--brand-soft)" size={15} />
            </span>
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Docs:</span>
            <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {currentItem ? currentItem.label : 'Table of Contents'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--brand-soft)', fontSize: 12, fontWeight: 600 }}>
            <span>Jump</span>
            <NavIcon name="chevron-down" color="var(--brand-soft)" size={13} />
          </div>
        </button>
      </div>

      {/* Mobile Slide-Up TOC Drawer */}
      {drawerOpen && (
        <>
          <div className="toc-drawer-backdrop" onClick={() => setDrawerOpen(false)} />
          <div className="toc-bottom-drawer">
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', letterSpacing: -0.2, display: 'flex', alignItems: 'center', gap: 8 }}>
                <NavIcon name="book-open" color="var(--brand-soft)" size={16} />
                <span>Documentation Index</span>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                style={{
                  background: 'rgba(245,241,232,0.06)',
                  border: 'none',
                  color: 'var(--text-dim)',
                  padding: '6px 12px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  minHeight: 36,
                }}
              >
                <span>Close</span>
                <NavIcon name="close" color="currentColor" size={12} />
              </button>
            </div>
            <div data-lenis-prevent style={{ padding: '16px 20px', overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', flex: 1 }}>
              {TOC_GROUPS.map((group) => (
                <div key={group.category} style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: group.themeSoft, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <NavIcon name={group.categoryIcon} color={group.themeColor} size={13} />
                    <span>{group.category}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {group.items.map(({ id, label, icon }) => {
                      const active = activeId === id;
                      return (
                        <button
                          key={id}
                          onClick={() => handleMobileSelect(id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 14px',
                            borderRadius: 8,
                            background: active ? 'rgba(59,130,246,0.12)' : 'transparent',
                            border: active ? '1px solid rgba(59,130,246,0.3)' : '1px solid transparent',
                            color: active ? '#ffffff' : 'var(--text-dim)',
                            fontSize: 13.5,
                            fontWeight: active ? 600 : 400,
                            textAlign: 'left',
                            cursor: 'pointer',
                            minHeight: 44,
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <NavIcon name={icon} color={active ? '#60a5fa' : 'var(--text-muted)'} size={14} />
                            <span>{label}</span>
                          </div>
                          {active && <NavIcon name="check" color="#10b981" size={14} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Full-Width Modern Hub Layout */}
      <div
        className="docs-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'clamp(260px, 19vw, 310px) minmax(0, 1fr)',
          alignItems: 'start',
          maxWidth: 1600,
          margin: '0 auto',
          padding: '0 clamp(16px, 4vw, 48px)',
          minHeight: '100vh',
          gap: 'clamp(24px, 3.5vw, 48px)',
          boxSizing: 'border-box',
          width: '100%',
        }}
      >
        {/* Animated & Color-Themed Sticky Desktop Sidebar */}
        <aside
          ref={sidebarRef}
          className="docs-sidebar custom-scrollbar"
          data-lenis-prevent
          style={{
            position: 'sticky',
            top: 80,
            alignSelf: 'start',
            height: 'calc(100vh - 96px)',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            paddingTop: 32,
            paddingBottom: 40,
            paddingRight: 16,
            borderRight: '1px solid var(--border)',
          }}
        >
          <div style={{
            fontSize: 10.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 2,
            color: 'var(--brand)',
            marginBottom: 22,
            paddingLeft: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--brand)' }}></span>
            Developer Hub
          </div>

          {TOC_GROUPS.map((group) => (
            <div key={group.category} className="toc-group-box" style={{ marginBottom: 24 }}>
              <div style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                color: group.themeSoft,
                marginBottom: 10,
                paddingLeft: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  borderRadius: 5,
                  background: group.badgeBg,
                  border: `1px solid ${group.themeColor}40`,
                  color: group.themeColor,
                  flexShrink: 0,
                }}>
                  <NavIcon name={group.categoryIcon} color={group.themeColor} size={12} />
                </span>
                {group.category}
              </div>

              {group.items.map(({ id, label, icon }) => {
                const active = activeId === id;
                return (
                  <button
                    key={id}
                    ref={(el) => (itemRefs.current[id] = el)}
                    className="toc-item-btn"
                    onClick={() => scrollTo(id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      width: '100%',
                      textAlign: 'left',
                      background: active
                        ? `linear-gradient(90deg, ${group.badgeBg} 0%, rgba(59,130,246,0.03) 100%)`
                        : 'transparent',
                      border: 'none',
                      borderLeft: active ? `3px solid ${group.themeColor}` : '3px solid transparent',
                      boxShadow: active ? `inset 0 0 14px ${group.badgeBg}` : 'none',
                      color: active ? 'var(--text)' : 'var(--text-muted)',
                      fontSize: 13,
                      fontWeight: active ? 700 : 400,
                      padding: '7px 10px',
                      borderRadius: active ? '0 8px 8px 0' : 8,
                      cursor: 'pointer',
                      fontFamily: 'var(--font-ui)',
                      marginBottom: 2,
                      position: 'relative',
                    }}
                  >
                    <span
                      className="icon-chip"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        background: active ? group.themeColor : 'var(--surface-hover)',
                        border: `1px solid ${active ? group.themeColor : 'var(--border)'}`,
                        boxShadow: active ? `0 0 12px ${group.themeColor}` : 'none',
                        color: active ? '#ffffff' : group.themeSoft,
                        flexShrink: 0,
                      }}
                    >
                      <NavIcon name={icon} color={active ? '#ffffff' : 'currentColor'} size={13} />
                    </span>
                    <span style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: active ? 'var(--text)' : 'inherit',
                      letterSpacing: active ? -0.1 : 0,
                    }}>
                      {label}
                    </span>
                    {active && (
                      <span style={{
                        marginLeft: 'auto',
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        background: group.themeColor,
                        boxShadow: `0 0 8px ${group.themeColor}`,
                        flexShrink: 0,
                      }}></span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </aside>

        {/* Main Reading & Reference Pane */}
        <main className="docs-main" style={{ paddingTop: 'clamp(20px, 4vw, 36px)', paddingBottom: 140, maxWidth: 960, width: '100%', minWidth: 0 }}>

          {/* ────────────────── SECTION 1: OVERVIEW & BASE URL ────────────────── */}
          <section id="overview" className="docs-section">
            
            {/* Clear Primary Headline */}
            <div style={{ marginBottom: 'clamp(24px, 4vw, 36px)' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 999,
                background: 'rgba(59,130,246,0.1)',
                border: '1px solid rgba(59,130,246,0.25)',
                color: 'var(--brand-soft)',
                fontSize: 12,
                fontWeight: 600,
                marginBottom: 14,
              }}>
                <NavIcon name="book-open" color="var(--brand-soft)" size={13} />
                <span>Developer Documentation &amp; API Reference</span>
              </div>
              <h1 style={{
                fontSize: 'clamp(28px, 4.5vw, 44px)',
                fontWeight: 700,
                letterSpacing: -1.2,
                lineHeight: 1.15,
                color: 'var(--text)',
                margin: '0 0 14px',
                fontFamily: 'var(--font-ui)',
                wordBreak: 'break-word',
              }}>
                Overview &amp; Base URL
              </h1>
              <p style={{
                color: 'var(--text-dim)',
                fontSize: 'clamp(14.5px, 1.8vw, 16px)',
                lineHeight: 1.75,
                maxWidth: 760,
                margin: 0,
              }}>
                Programmatically compile Python scripts and project zip archives into custom virtualized binaries (<InlineCode>.pyd</InlineCode> / <InlineCode>.so</InlineCode>). Automated for CI/CD pipelines with Free, Beta, and Premium tier integration.
              </p>
            </div>

            <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.75, marginBottom: 16 }}>
              PyK7VM provides two verified paths for securing Python IP:
            </p>
            <ul style={{ color: 'var(--text-dim)', fontSize: 14.5, lineHeight: 1.8, paddingLeft: 22, marginBottom: 24 }}>
              <li><strong style={{ color: 'var(--text)' }}>Automatic Virtualization</strong>: The compiler auto-detects security-critical algorithms, licensing routines, and cryptographic operations in your source and virtualizes them into a polymorphic native VM.</li>
              <li><strong style={{ color: 'var(--text)' }}>REST API v1 Pipeline</strong>: Asynchronously compile single scripts or multi-file zip packages into protected native binaries from GitHub Actions, GitLab CI, or custom CLI scripts.</li>
            </ul>

            <div style={{
              background: 'rgba(6,6,12,0.85)',
              border: '1px solid var(--border-strong)',
              borderRadius: 12,
              padding: '16px 20px',
              marginBottom: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 12,
              maxWidth: '100%',
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <span style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>REST API Base URL</span>
                <span style={{ fontFamily: 'var(--font-code)', fontSize: 'clamp(13px, 2vw, 15px)', color: '#93c5fd', fontWeight: 600, wordBreak: 'break-all' }}>https://pyk7vm.com/api/v1</span>
              </div>
              <CopyButton textToCopy="https://pyk7vm.com/api/v1" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
              {[
                ['HTTPS TLS 1.3 Only', 'All API communication requires encrypted transport. Plain HTTP traffic is dropped.'],
                ['JSON Responses', 'All v1 endpoints emit application/json payloads (except the binary stream job_download.php).'],
                ['Bearer API Key', 'Authentication via Bearer token in the standard HTTP Authorization header.'],
                ['Native Extension Outputs', 'Yields sealed .pyd (Windows), .so (Linux), or .exe binaries compiled for Python 3.11-3.13 on x64 and ARM64 (plus free-threaded 3.13t on Windows x64).'],
              ].map(([t, d]) => (
                <div key={t} style={{ background: 'rgba(245,241,232,0.02)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 18px' }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>{t}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.6 }}>{d}</div>
                </div>
              ))}
            </div>
          </section>

          {/* ────────────────── SECTION 2: QUICKSTART & CLI ────────────────── */}
          <section id="quickstart-cli" className="docs-section">
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 600, letterSpacing: -0.6, color: 'var(--text)', marginBottom: 16, marginTop: 0 }}>Quickstart &amp; CLI Guide</h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.75, marginBottom: 20 }}>
              The compiler automatically detects sensitive functions and compiles their AST into custom randomized stack machine bytecode. Submit your source and the engine handles protection automatically:
            </p>

            <MultiLangCodeBlock
              title="Python Decorator Comparison"
              snippets={{
                plain: CODE_DECORATOR_BEFORE,
                decorated: CODE_DECORATOR_AFTER,
              }}
            />

            <h3 style={{ fontSize: 'clamp(16px, 2.2vw, 19px)', fontWeight: 600, color: 'var(--text)', marginTop: 36, marginBottom: 14 }}>
              Command-Line Compiler Utility
            </h3>
            <p style={{ color: 'var(--text-dim)', fontSize: 14.5, lineHeight: 1.75, marginBottom: 16 }}>
              Run the PyK7VM compiler directly from your development terminal or build script:
            </p>

            <SingleCodeBlock code={CODE_CLI_BASIC} title="Terminal CLI" />

            <h4 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginTop: 24, marginBottom: 12 }}>
              CLI Compilation Options
            </h4>
            <ParamTable>
              <ParamRow name="--platform" type="string" required={false} desc={<>Target platform: <InlineCode>windows</InlineCode> (compiles <InlineCode>.pyd</InlineCode>) or <InlineCode>linux</InlineCode> (compiles <InlineCode>.so</InlineCode>).</>} />
              <ParamRow name="--py" type="string" required={false} desc={<>Target Python ABI: <InlineCode>3.11</InlineCode>, <InlineCode>3.12</InlineCode>, <InlineCode>3.13</InlineCode>, or <InlineCode>3.13t</InlineCode> (free-threaded).</>} />
              <ParamRow name="--profile" type="string" required={false} desc={<>Protection profile: <InlineCode>free</InlineCode>, <InlineCode>release</InlineCode>, <InlineCode>premium</InlineCode>, or <InlineCode>paranoid</InlineCode>.</>} />
              <ParamRow name="--zip" type="boolean" required={false} desc="Package multi-file projects into a protected zip archive." />
              <ParamRow name="--output, -o" type="string" required={false} desc="Target path for output binary or archive." />
              <ParamRow name="--key" type="string (env: $PK7VM_API_KEY)" required={false} desc="API key (pk7vm_...). Falls back to the PK7VM_API_KEY environment variable." />
            </ParamTable>
          </section>

          {/* ────────────────── SECTION 3: AUTHENTICATION ────────────────── */}
          <section id="authentication" className="docs-section">
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 600, letterSpacing: -0.6, color: 'var(--text)', marginBottom: 16, marginTop: 0 }}>Authentication &amp; API Keys</h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.75, marginBottom: 20 }}>
              All REST API endpoints require a Bearer token in the HTTP <InlineCode>Authorization</InlineCode> header. API keys are available on <strong style={{ color: '#93c5fd' }}>Beta</strong> and <strong style={{ color: '#fbbf24' }}>Premium</strong> plans (managed from <a href="account.php" style={{ color: '#93c5fd', textDecoration: 'underline' }}>Account Settings</a>).
            </p>

            <SingleCodeBlock code={CODE_AUTH_HEADER} title="HTTP Header" />

            <div style={{
              background: 'rgba(59,130,246,0.06)',
              border: '1px solid rgba(59,130,246,0.2)',
              borderLeft: '4px solid var(--brand)',
              borderRadius: 8,
              padding: '16px 20px',
              marginTop: 20,
              fontSize: 13.5,
              color: 'var(--text-dim)',
              lineHeight: 1.7,
            }}>
              <strong style={{ color: '#93c5fd' }}>Verified Plan Rule:</strong> API keys are associated with your account plan resolved server-side. Free tier accounts use the web UI; Beta and Premium accounts have full programmatic REST API access.
            </div>
          </section>

          {/* ────────────────── SECTION 4: SUBMIT JOB ────────────────── */}
          <section id="submit" className="docs-section">
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 600, letterSpacing: -0.6, color: 'var(--text)', marginBottom: 16, marginTop: 0 }}>1. Submit Build Job</h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.75, marginBottom: 20 }}>
              Upload a Python source file (<InlineCode>.py</InlineCode>) or a project zip (<InlineCode>.zip</InlineCode>) via multipart/form-data. The parameter field name is <InlineCode>file</InlineCode>.
            </p>

            <EndpointCard method="POST" path="/api/v1/job_submit.php" desc="Content-Type: multipart/form-data" />

            <ParamTable>
              <ParamRow name="file" type="file / binary" required desc="The .py source file or .zip project archive to compile." />
              <ParamRow name="module_name" type="string" required={false} desc="Custom output module filename (without extension). Defaults to main." />
              <ParamRow name="options" type="string (JSON)" required={false} desc="Optional JSON object of build flags, e.g. { 'generate_exe': true, 'anti_debug': true }. Full surface: see Build Options Reference below." />
              <ParamRow name="hot_paths" type="string (JSON array)" required={false} desc="Optional JSON array of function names to compile natively with Cython." />
            </ParamTable>

            <MultiLangCodeBlock
              title="Submit Job Code Snippets"
              snippets={{
                curl: CODE_SUBMIT_CURL,
                python: CODE_SUBMIT_PYTHON,
                node: CODE_SUBMIT_NODE,
              }}
            />

            <div style={{ fontSize: 12.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-muted)', marginBottom: 10 }}>Response: 200 OK</div>
            <SingleCodeBlock code={CODE_SUBMIT_RESPONSE} title="JSON Response (200)" />
          </section>

          {/* ────────────────── SECTION 4B: BUILD OPTIONS REFERENCE ────────────────── */}
          <section id="build-options" className="docs-section">
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 600, letterSpacing: -0.6, color: 'var(--text)', marginBottom: 16, marginTop: 0 }}>Build Options Reference</h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.75, marginBottom: 14 }}>
              Every field below is a key inside the <InlineCode>options</InlineCode> JSON object passed to <InlineCode>job_submit.php</InlineCode>. Unknown keys are ignored.
              Options are enforced server-side per <strong>plan</strong> (your account tier: Free, Beta, Premium). Rows marked <em>Beta / Premium</em> are ignored on Free, which builds with the fixed Free stack (Python 3.13, no native compilation, no packaging extras).
            </p>
            <SingleCodeBlock code={CODE_OPTIONS_EXAMPLE} title="Full options payload (Beta / Premium)" />

            <h4 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginTop: 26, marginBottom: 12 }}>Protection</h4>
            <ParamTable>
              <ParamRow name="anti_debug" type="bool" required={false} desc="Debugger detection traps (PEB, DR registers, timing). Default: on for release/paranoid presets." />
              <ParamRow name="anti_hook" type="bool" required={false} desc="Critical-API hook detection + integrity heartbeat." />
              <ParamRow name="anti_vm" type="bool" required={false} desc="Virtual-machine / sandbox detection. Off by default (Hyper-V hosts can trip it)." />
              <ParamRow name="ast_mutation" type="bool" required={false} desc="AST-level code renaming / structure mutation before compilation." />
              <ParamRow name="bytecode_mutation" type="bool" required={false} desc="VM instruction shuffling + opcode epoch dispatch." />
              <ParamRow name="mutation_intensity" type={'"low" | "medium" | "high"'} required={false} desc="Strength budget for the mutation passes above. Default: medium." />
              <ParamRow name="hwid_binding" type="bool" required={false} desc="Bind the protected binary to one machine fingerprint (HWID). Beta / Premium." />
            </ParamTable>

            <h4 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginTop: 26, marginBottom: 12 }}>Function Selection</h4>
            <ParamTable>
              <ParamRow name="cython_mode" type={'"off" | "auto" | "manual"'} required={false} desc="Native (Cython) compilation of selected functions. auto = engine picks hot paths, manual = only the listed targets. Legacy boolean cython=true maps to auto." />
              <ParamRow name="cython_targets" type="string[]" required={false} desc="Function names for manual native compilation (mirrors the top-level hot_paths field)." />
              <ParamRow name="vm_skip_targets" type="string[]" required={false} desc="Functions compiled natively, exempt from VM protection (partial protection for hot loops). Beta / Premium." />
              <ParamRow name="aot_targets" type="string[]" required={false} desc="Functions compiled through the AOT transpiler (native critical-path, no VM). Beta / Premium." />
              <ParamRow name="exports" type="string[]" required={false} desc={'Explicit module exports for pyd builds, as "name" or "name:alias" (valid Python identifiers).'} />
            </ParamTable>

            <h4 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginTop: 26, marginBottom: 12 }}>Output</h4>
            <ParamTable>
              <ParamRow name="target_python" type={'"3.11" | "3.12" | "3.13" | "3.13t"'} required={false} desc="Interpreter ABI of the produced extension (3.13t = free-threaded). Locked to 3.13 on Free." />
              <ParamRow name="platform" type={'"windows" | "linux" | "both"'} required={false} desc="Target platform(s). Default: windows." />
              <ParamRow name="generate_exe" type="bool" required={false} desc="Produce a standalone .exe (embedded launcher + runtime) instead of a .pyd." />
              <ParamRow name="include_launcher" type="bool" required={false} desc="EXE only: ship a run_<module>.py launcher alongside the artifact." />
              <ParamRow name="window_mode" type={'"console" | "windowed"'} required={false} desc="EXE only: windowed links the GUI subsystem (no console window). Default: console." />
            </ParamTable>

            <h4 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginTop: 26, marginBottom: 12 }}>Packaging Extras</h4>
            <ParamTable>
              <ParamRow name="bundle_init" type="bool" required={false} desc="Include a __init__.py stub making the artifact importable as a package." />
              <ParamRow name="bundle_example" type="bool" required={false} desc="Include a usage example script." />
              <ParamRow name="bundle_requirements" type="bool" required={false} desc="Include a requirements.txt." />
              <ParamRow name="pyi_stub" type="bool" required={false} desc="PEP 561 typed-package .pyi stub. Beta / Premium." />
              <ParamRow name="wheel" type="bool" required={false} desc="Build a pip-installable wheel (with py.typed marker). Beta / Premium." />
              <ParamRow name="wheel_version" type="string (PEP 440)" required={false} desc="Wheel version, e.g. 1.2.3 or 0.1.0rc1. Default: 0.1.0. Requires wheel: true." />
            </ParamTable>

            <h4 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginTop: 26, marginBottom: 12 }}>Operator Alerts</h4>
            <ParamTable>
              <ParamRow name="webhook_url" type="string (URL)" required={false} desc="Discord webhook receiving runtime security alerts (tamper, debugger, dump attempts). Windows builds only." />
              <ParamRow name="webhook_name" type="string" required={false} desc="Display name for alert embeds. Default: PyK7VM." />
            </ParamTable>
          </section>

          {/* ────────────────── SECTION 5: CHECK STATUS ────────────────── */}
          <section id="status" className="docs-section">
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 600, letterSpacing: -0.6, color: 'var(--text)', marginBottom: 16, marginTop: 0 }}>2. Poll Job Status</h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.75, marginBottom: 20 }}>
              Poll status with your <InlineCode>job_id</InlineCode>. On completion a one-time <InlineCode>download_token</InlineCode> is issued; the v1 API then serves the artifact from <InlineCode>job_download.php?job_id=...</InlineCode> (no separate token parameter needed).
            </p>

            <EndpointCard method="GET" path="/api/v1/job_status.php?job_id=..." desc="Accept: application/json" />

            <ParamTable>
              <ParamRow name="job_id" type="string (hex)" required desc="32-character hexadecimal job ID returned by job_submit.php." />
            </ParamTable>

            <MultiLangCodeBlock
              title="Poll Status Code Snippets"
              snippets={{
                curl: CODE_STATUS_CURL,
                python: CODE_STATUS_PYTHON,
                node: CODE_STATUS_NODE,
              }}
            />

            <div style={{ fontSize: 12.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-muted)', marginBottom: 10 }}>Response: 200 OK</div>
            <SingleCodeBlock code={CODE_STATUS_RESPONSE} title="JSON Response (200)" />

            <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginTop: 28, marginBottom: 14 }}>
              Verified Status Values
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                ['queued',           '#94a3b8', 'rgba(148,163,184,0.12)', 'Job is in the build queue awaiting an available worker slot.'],
                ['building',         '#60a5fa', 'rgba(59,130,246,0.12)',  'Compiler daemon is actively virtualizing AST and linking C++ extension.'],
                ['hot_path_pending', '#fbbf24', 'rgba(245,158,11,0.12)',  'Prescan detected hot paths; awaiting confirmation via job_hotpath.php.'],
                ['complete',         '#34d399', 'rgba(16,185,129,0.12)',  'Build finished successfully. download_token is generated; fetch via job_download.php?job_id=.'],
                ['error',            '#f87171', 'rgba(248,113,113,0.12)', 'Compilation failed. See error string in payload for detailed cause.'],
              ].map(([st, col, bg, desc]) => (
                <div key={st} style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: 'rgba(245,241,232,0.015)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  padding: '10px 16px',
                  gap: 14,
                  flexWrap: 'wrap',
                }}>
                  <span style={{ fontFamily: 'var(--font-code)', fontSize: 12, fontWeight: 700, color: col, background: bg, padding: '3px 9px', borderRadius: 4, whiteSpace: 'nowrap' }}>
                    {st}
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.5, flex: 1, minWidth: 200 }}>
                    {desc}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* ────────────────── SECTION 6: HOT PATHS ────────────────── */}
          <section id="hotpath" className="docs-section">
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 600, letterSpacing: -0.6, color: 'var(--text)', marginBottom: 16, marginTop: 0 }}>3. Confirm Hot Paths (Optional)</h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.75, marginBottom: 20 }}>
              When status is <InlineCode>hot_path_pending</InlineCode>, post an array of function names from <InlineCode>functions</InlineCode> to compile them natively into C-extensions for performance.
            </p>

            <EndpointCard method="POST" path="/api/v1/job_hotpath.php" desc="Content-Type: application/json" />

            <ParamTable>
              <ParamRow name="job_id" type="string" required desc="The active build job ID." />
              <ParamRow name="hot_paths" type="string[]" required desc="Array of function names to Cython-compile. Pass [] to skip." />
            </ParamTable>

            <MultiLangCodeBlock
              title="Hot Path Confirmation Snippets"
              snippets={{
                curl: CODE_HOTPATH_CURL,
                python: CODE_HOTPATH_PYTHON,
                node: CODE_HOTPATH_NODE,
              }}
            />

            <div style={{
              background: 'rgba(59,130,246,0.05)',
              border: '1px solid rgba(59,130,246,0.25)',
              borderLeft: '4px solid var(--brand)',
              borderRadius: 8,
              padding: '16px 20px',
              fontSize: 13.5,
              color: 'var(--text-dim)',
              lineHeight: 1.7,
              margin: '20px 0',
            }}>
              <strong style={{ color: 'var(--brand-soft)' }}>Why use Hot Path optimization?</strong> CPU-bound mathematical routines (e.g. matrix math, cryptographic hashing loops, image filters) can experience interpretative overhead inside a polymorphic virtual machine. PyK7VM's prescan detects compute-intensive function bodies and lets you compile them directly to native C-speed via Cython compilation. This keeps VM-based protection on sensitive orchestration and licensing logic while still running hot loops at native speed.
            </div>

            <div style={{ fontSize: 12.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-muted)', marginBottom: 10 }}>Response: 200 OK</div>
            <SingleCodeBlock code={`{\n  <span style="color:#34d399">"ok"</span>: <span style="color:#60a5fa">true</span>,\n  <span style="color:#34d399">"job_id"</span>: <span style="color:#fbbf24">"a3f9bc12e4561234a3f9bc12e4561234"</span>,\n  <span style="color:#34d399">"hot_paths"</span>: [<span style="color:#fbbf24">"verify_license"</span>]\n}`} title="JSON Response" />
          </section>

          {/* ────────────────── SECTION 7: DOWNLOAD ────────────────── */}
          <section id="download" className="docs-section">
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 600, letterSpacing: -0.6, color: 'var(--text)', marginBottom: 16, marginTop: 0 }}>4. Download Protected Artifact</h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.75, marginBottom: 20 }}>
              Once the job status reaches <InlineCode>complete</InlineCode>, download the compiled binary archive (<InlineCode>.zip</InlineCode> containing <InlineCode>.pyd</InlineCode> or <InlineCode>.so</InlineCode>).
            </p>

            <EndpointCard method="GET" path="/api/v1/job_download.php?job_id=..." desc="Content-Type: application/zip" />

            <ParamTable>
              <ParamRow name="job_id" type="string" required desc="The completed job ID." />
            </ParamTable>

            <MultiLangCodeBlock
              title="Download Artifact Snippets"
              snippets={{
                curl: CODE_DOWNLOAD_CURL,
                python: CODE_DOWNLOAD_PYTHON,
                node: CODE_DOWNLOAD_NODE,
              }}
            />

            <div style={{
              background: 'rgba(245,158,11,0.06)',
              border: '1px solid rgba(245,158,11,0.25)',
              borderLeft: '4px solid #fbbf24',
              borderRadius: 8,
              padding: '16px 20px',
              fontSize: 13.5,
              color: 'var(--text-dim)',
              lineHeight: 1.7,
            }}>
              <strong style={{ color: '#fbbf24' }}>One-Time Download Link:</strong> The downloaded zip file is unlinked immediately after successful streaming, with a maximum 1-hour TTL on server storage.
            </div>
          </section>

          {/* ────────────────── SECTION 8: FULL LIFECYCLE FLOW ────────────────── */}
          <section id="workflow" className="docs-section">
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 600, letterSpacing: -0.6, color: 'var(--text)', marginBottom: 16, marginTop: 0 }}>Full Lifecycle Sequence</h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.75, marginBottom: 20 }}>
              End-to-end client-server interaction during an automated build lifecycle:
            </p>
            <LifecycleStepper onNavigate={scrollTo} />
          </section>

          {/* ────────────────── SECTION 9: GITHUB ACTIONS ────────────────── */}
          <section id="github-actions" className="docs-section">
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 600, letterSpacing: -0.6, color: 'var(--text)', marginBottom: 16, marginTop: 0 }}>CI/CD: GitHub Actions Recipe</h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.75, marginBottom: 20 }}>
              Automate Python protection in GitHub Actions on every tag push:
            </p>
            <SingleCodeBlock code={CODE_GITHUB_ACTIONS} title=".github/workflows/protect.yml" />
          </section>

          {/* ────────────────── SECTION 10: GITLAB CI ────────────────── */}
          <section id="gitlab-ci" className="docs-section">
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 600, letterSpacing: -0.6, color: 'var(--text)', marginBottom: 16, marginTop: 0 }}>CI/CD: GitLab CI Pipeline</h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.75, marginBottom: 20 }}>
              GitLab CI/CD job definition using Python 3.11 container and secret variable <InlineCode>PK7VM_API_KEY</InlineCode>:
            </p>
            <SingleCodeBlock code={CODE_GITLAB_CI} title=".gitlab-ci.yml" />
          </section>

          {/* ────────────────── SECTION 11: PYTHON CLIENT SDK ────────────────── */}
          <section id="python-sdk" className="docs-section">
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 600, letterSpacing: -0.6, color: 'var(--text)', marginBottom: 16, marginTop: 0 }}>Python Client SDK</h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.75, marginBottom: 20 }}>
              Zero-dependency Python wrapper for submitting, polling, and downloading protected builds in custom scripts:
            </p>
            <SingleCodeBlock code={CODE_FULL_PYTHON_CLIENT} title="pyk7vm_client.py" />
          </section>

          {/* ────────────────── SECTION 12: RATE LIMITS ────────────────── */}
          <section id="rate-limits" className="docs-section">
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 600, letterSpacing: -0.6, color: 'var(--text)', marginBottom: 16, marginTop: 0 }}>Rate Limits &amp; Plan Quotas</h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.75, marginBottom: 20 }}>
              PyK7VM provides exactly 3 tiers (<strong style={{ color: 'var(--text-muted)' }}>Free</strong>, <strong style={{ color: '#93c5fd' }}>Beta</strong>, and <strong style={{ color: '#fbbf24' }}>Premium</strong>) with verified system limits:
            </p>

            <div className="table-responsive-wrapper custom-scrollbar" style={{ borderRadius: 10, border: '1px solid var(--border)', overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: 24, maxWidth: '100%' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                <thead>
                  <tr style={{ background: 'rgba(245,241,232,0.03)' }}>
                    {['Plan Tier', 'API Key Access', 'Max Upload Size', 'Build Rate Limit', 'Concurrency', 'Runtime Watermark'].map((h) => (
                      <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Free', 'Web UI Only', '100 MB (.py only)', '3 / hour', '1 job', 'Yes (Watermarked)'],
                    ['Beta', 'Full REST API v1', '200 MB (.zip allowed)', 'Unlimited', '2 concurrent jobs', 'Yes (Watermarked)'],
                    ['Premium', 'Full REST API v1', '500 MB (.zip allowed)', 'Unlimited', '2 concurrent jobs', 'No (Clean Silent)'],
                  ].map(([plan, api, maxUpload, rate, workers, watermark], idx) => (
                    <tr key={plan} style={{ background: idx === 2 ? 'rgba(245,158,11,0.03)' : idx === 1 ? 'rgba(59,130,246,0.03)' : 'transparent' }}>
                      <td style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)', fontSize: 13.5, color: idx === 2 ? '#fbbf24' : idx === 1 ? '#93c5fd' : 'var(--text-muted)', fontWeight: 700, whiteSpace: 'nowrap' }}>{plan}</td>
                      <td style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)', fontSize: 13, color: 'var(--text)', whiteSpace: 'nowrap' }}>{api}</td>
                      <td style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--font-code)', fontSize: 12.5, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{maxUpload}</td>
                      <td style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--font-code)', fontSize: 12.5, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{rate}</td>
                      <td style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--font-code)', fontSize: 12.5, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{workers}</td>
                      <td style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)', fontSize: 12.5, color: idx === 2 ? '#34d399' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>{watermark}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h4 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginTop: 24, marginBottom: 12 }}>
              Rate Limit Response Headers
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <InlineCode>X-RateLimit-Remaining</InlineCode>
                <span style={{ fontSize: 13.5, color: 'var(--text-dim)' }}>Remaining API call budget within the current sliding window.</span>
              </div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <InlineCode>X-RateLimit-Reset</InlineCode>
                <span style={{ fontSize: 13.5, color: 'var(--text-dim)' }}>Unix timestamp when the rate limit window refreshes.</span>
              </div>
            </div>
          </section>

          {/* ────────────────── SECTION 13: ERROR CODES ────────────────── */}
          <section id="errors" className="docs-section" style={{ borderBottom: 'none', marginBottom: 0, paddingBottom: 32 }}>
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 600, letterSpacing: -0.6, color: 'var(--text)', marginBottom: 16, marginTop: 0 }}>Error Codes Matrix</h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.75, marginBottom: 20 }}>
              All error responses follow a uniform JSON schema to simplify exception handling:
            </p>

            <SingleCodeBlock code={CODE_ERROR_RESPONSE} title="Standard Error Schema" />

            <div className="table-responsive-wrapper custom-scrollbar" style={{ borderRadius: 10, border: '1px solid var(--border)', overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginTop: 20, maxWidth: '100%' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 540 }}>
                <thead>
                  <tr style={{ background: 'rgba(245,241,232,0.03)' }}>
                    {['Status', 'Code String', 'Description / Recovery Action'].map((h) => (
                      <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['400', 'invalid_params',   'Missing mandatory parameters or malformed file payload.'],
                    ['401', 'unauthorized',      'Missing, expired, or invalid API key Bearer token.'],
                    ['403', 'forbidden',         'Account plan lacks permission (e.g. zip upload on Free tier).'],
                    ['404', 'not_found',         'The requested job_id does not exist or has already expired.'],
                    ['405', 'method_not_allowed','HTTP method not allowed on this endpoint (e.g. GET on POST endpoint).'],
                    ['409', 'conflict',          'Action invalid for current build state (e.g. download while building).'],
                    ['410', 'gone',              'Single-use download token consumed or 1-hour TTL expired.'],
                    ['413', 'payload_too_large', 'Upload exceeds your plan size limit (Free 100 MB, Beta 200 MB, Premium 500 MB).'],
                    ['429', 'rate_limited',      'Exceeded rate limits or concurrent active jobs quota.'],
                    ['500', 'server_error',      'Compiler internal exception. Check syntax and contact support on Discord.'],
                  ].map(([status, code, desc]) => (
                    <tr key={status}>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--font-code)', fontSize: 13, color: '#f87171', fontWeight: 700, whiteSpace: 'nowrap' }}>{status}</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--font-code)', fontSize: 12.5, color: '#93c5fd', whiteSpace: 'nowrap' }}>{code}</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', color: 'var(--text-dim)', fontSize: 13, lineHeight: 1.6 }}>{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}

// ─── ROOT APP COMPONENT ─────────────────────────────────────────────────────

function DocsApp() {
  const [lang, setLang] = useState('curl');

  // Initialize Lenis momentum smooth scrolling
  useEffect(() => {
    if (typeof window.Lenis !== 'undefined') {
      const lenis = new window.Lenis({
        duration: 0.8,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        touchMultiplier: 1.5,
      });
      window.__lenis = lenis;

      let rafId;
      function raf(time) {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      }
      rafId = requestAnimationFrame(raf);

      return () => {
        cancelAnimationFrame(rafId);
        lenis.destroy();
      };
    }
  }, []);

  return (
    <LangContext.Provider value={{ lang, setLang }}>
      <div style={{ position: 'relative', minHeight: '100vh', background: 'var(--bg)', maxWidth: '100vw', overflowX: 'clip' }}>
        <style>{`
          .docs-code-box {
            background: #090a12 !important;
            border: 1px solid rgba(255, 255, 255, 0.12) !important;
            box-shadow: 0 16px 40px rgba(0, 0, 0, 0.35), 0 0 20px rgba(59, 130, 246, 0.06) !important;
          }
          .docs-code-head {
            background: #11131f !important;
            border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
          }
          .docs-code-pre {
            background: #090a12 !important;
            color: #f8fafc !important;
          }
          html.light .docs-sidebar {
            background: #ffffff !important;
            border-right-color: rgba(15, 23, 42, 0.08) !important;
          }
          html.light .docs-endpoint-card {
            background: #ffffff !important;
            border-color: rgba(15, 23, 42, 0.08) !important;
            box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04) !important;
          }
          html.light .docs-lifecycle-card {
            background: #ffffff !important;
            border-color: rgba(15, 23, 42, 0.08) !important;
            box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05) !important;
          }
          html.light .docs-return-pill {
            background: #f8fafc !important;
            border-color: rgba(15, 23, 42, 0.08) !important;
            color: #334155 !important;
          }
          html.light .docs-stepper-btn {
            background: #f8fafc !important;
            border-color: rgba(15, 23, 42, 0.08) !important;
            color: #475569 !important;
          }
          html.light .docs-stepper-btn:hover {
            background: #f1f5f9 !important;
            color: #0f172a !important;
          }
          html.light .table-responsive-wrapper table {
            background: #ffffff !important;
          }
          html.light .table-responsive-wrapper th {
            background: #f8fafc !important;
            color: #0f172a !important;
            border-bottom-color: rgba(15, 23, 42, 0.08) !important;
          }
          html.light .table-responsive-wrapper td {
            border-bottom-color: rgba(15, 23, 42, 0.06) !important;
          }
        `}</style>
        <Nav />
        <DocsLayout />
        <Footer />
        <DiscordCTA />
      </div>
    </LangContext.Provider>
  );
}

// Mount cleanly via React 18 createRoot
ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(DocsApp));
